import {useEffect, useRef, useState} from 'react';
import {decideFulfillmentAuthorization, loadPendingFulfillmentAuthorizations, PendingFulfillmentAuthorization} from './api';

function confirmsDecision(value: unknown, item: PendingFulfillmentAuthorization, decision: 'accept' | 'decline'): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const receipt = value as Record<string, unknown>;
  return receipt.authorization_id === item.authorization_id
    && receipt.state === (decision === 'accept' ? 'active' : 'declined')
    && Number.isSafeInteger(item.version) && item.version > 0
    && Number.isSafeInteger(receipt.version) && receipt.version === item.version + 1
    && typeof receipt.created === 'boolean';
}

export function CompanyFulfillmentDecisions({onAccepted}: {onAccepted?: () => void} = {}) {
  const [items, setItems] = useState<PendingFulfillmentAuthorization[]>([]);
  const [status, setStatus] = useState('Loading work awaiting company decision…');
  const [pending, setPending] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const busy = useRef(true);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const focusReloadResult = useRef(false);
  useEffect(() => {
    if (!pending && focusReloadResult.current) {
      focusReloadResult.current = false;
      statusRef.current?.focus();
    }
  }, [pending]);
  useEffect(() => {
    let active = true;
    busy.current = true; setPending(true); setItems([]); setStatus('Loading work awaiting company decision…');
    loadPendingFulfillmentAuthorizations().then(next => {
      if (!active) return;
      setItems(next); setStatus(next.length ? 'Review work routed to your company.' : 'No work is awaiting a company decision.');
    }).catch(() => {if (active) setStatus('Company decisions could not be loaded. Reload company decisions.');})
      .finally(() => {if (active) {busy.current = false; setPending(false);}});
    return () => {active = false;};
  }, [reloadKey]);

  async function decide(item: PendingFulfillmentAuthorization, decision: 'accept' | 'decline') {
    if (busy.current) return;
    busy.current = true; setPending(true); setStatus('Recording company decision…');
    let saved = false;
    try {
      const receipt = await decideFulfillmentAuthorization(item.authorization_id, item.version, decision);
      if (!confirmsDecision(receipt, item, decision)) throw new Error('Company decision receipt mismatch.');
      saved = true;
      if (decision === 'accept') onAccepted?.();
      setItems(await loadPendingFulfillmentAuthorizations());
      setStatus(decision === 'accept' ? 'Work accepted. It is ready for internal assignment.' : 'Work declined. ProFenz Mission Control owns the coverage exception.');
    } catch {
      setItems([]);
      setStatus(saved
        ? 'The company decision was saved, but the pending queue could not be loaded. Reload company decisions.'
        : 'The company decision result could not be confirmed. Reload company decisions before taking another action.');
    } finally {busy.current = false; setPending(false);}
  }
  return <section className="panel" aria-labelledby="company-decision-heading">
    <h2 id="company-decision-heading">Company acceptance</h2>
    <p ref={statusRef} role="status" aria-live="polite" tabIndex={-1}>{status}</p>
    <button type="button" disabled={pending} onClick={() => {focusReloadResult.current = true; setReloadKey(key => key + 1);}}>Reload company decisions</button>
    <ul>{items.map(x => <li key={x.authorization_id}>
      {x.service_type_code} due {x.deadline_date} — {x.city}, {x.admin_area}
      <button disabled={pending} onClick={() => void decide(x, 'accept')}>Accept work</button>
      <button disabled={pending} onClick={() => void decide(x, 'decline')}>Decline work</button>
    </li>)}</ul>
  </section>;
}
